import { type ComponentType, type SVGProps, useState } from 'react'

import { DetailIcon, IconButton, Menu, MenuContent, MenuItem, MenuTrigger } from '@ds/components'

import { AsyncAudioActionsDialog } from '../audio-actions-dialog'

import styles from './AudioCardActionsMenu.module.css'

export type TAudioCardMenuItem = {
    id: string
    label: string
    Icon?: ComponentType<SVGProps<SVGSVGElement>>
    onSelect: () => void
}

type TAudioCardActionsMenuProps = {
    items: TAudioCardMenuItem[]
    /** audio-web's mobile sheet carries a shorter list than the desktop menu; defaults to `items`. */
    sheetItems?: TAudioCardMenuItem[]
    triggerLabel: string
    sheetTitle: string
    cancelLabel: string
    onTriggerIntent?: () => void
}

export const AudioCardActionsMenu = ({
    items,
    sheetItems,
    triggerLabel,
    sheetTitle,
    cancelLabel,
    onTriggerIntent,
}: TAudioCardActionsMenuProps) => {
    const [isSheetOpen, setIsSheetOpen] = useState(false)
    const sheetActions = sheetItems ?? items

    const handleSheetIntent = () => {
        AsyncAudioActionsDialog.preload()
        onTriggerIntent?.()
    }

    const handleSheetOpen = () => {
        setIsSheetOpen(true)
    }

    if (items.length === 0) {
        return null
    }

    return (
        <>
            <span className={styles.wide}>
                <Menu modal={false}>
                    <MenuTrigger asChild>
                        <IconButton
                            variant="outline"
                            Icon={DetailIcon}
                            aria-label={triggerLabel}
                            data-test="more-button"
                            onPointerEnter={onTriggerIntent}
                            onPointerDown={onTriggerIntent}
                            onFocus={onTriggerIntent}
                        />
                    </MenuTrigger>
                    <MenuContent>
                        {items.map(item => (
                            <MenuItem
                                key={item.id}
                                onSelect={item.onSelect}
                                className={styles.item}
                                data-test={`audio-action-${item.id}`}
                            >
                                {item.Icon ? <item.Icon className={styles.icon} /> : null}
                                {item.label}
                            </MenuItem>
                        ))}
                    </MenuContent>
                </Menu>
            </span>
            <span className={styles.narrow}>
                <IconButton
                    variant="outline"
                    Icon={DetailIcon}
                    aria-label={triggerLabel}
                    onPointerEnter={handleSheetIntent}
                    onPointerDown={handleSheetIntent}
                    onFocus={handleSheetIntent}
                    onClick={handleSheetOpen}
                />
            </span>
            <AsyncAudioActionsDialog
                open={isSheetOpen}
                onOpenChange={setIsSheetOpen}
                title={sheetTitle}
                cancelLabel={cancelLabel}
                items={sheetActions}
            />
        </>
    )
}
